import {inject, Injectable} from '@angular/core';
import {Route} from '@angular/router';
import {IframeComponent} from '../components/iframe/iframe.component';
import {WebComponentComponent} from '../components/web-component/web-component.component';
import {ConfigurationService} from '../core/configuration/services/configuration.service';
import {UserData} from '../core/user/models/user-data';
import {UserService} from '../core/user/services/user.service';
import {IIframeAppConfiguration} from "../interfaces/IIframeAppConfiguration";
import {IWebComponentAppConfiguration} from "../interfaces/IWebComponentAppConfiguration";
import {WebComponentAttributeService} from "../integration/web-component-attribute.service";
import {IframeParamService} from "../integration/iframe-param.service";
import {WebComponentEventService} from "../integration/web-component-event.service";

@Injectable({
  providedIn: 'root'
})
export class RouteFactoryService {
  private readonly iframeParamService: IframeParamService = inject(IframeParamService);
  private readonly webComponentAttributeService: WebComponentAttributeService = inject(WebComponentAttributeService);
  private readonly webComponentEventService: WebComponentEventService = inject(WebComponentEventService);
  private readonly userService: UserService = inject(UserService);

  user: UserData;

  constructor() {
    this.userService.user$.subscribe((user: UserData) => this.user = user);
  }

  static isIframeApp(app: IIframeAppConfiguration | IWebComponentAppConfiguration): app is IIframeAppConfiguration {
    return app && (app as IIframeAppConfiguration).iFrameUrls !== undefined;
  }

  getRouteForApp(app: IIframeAppConfiguration | IWebComponentAppConfiguration): Route {
    if (RouteFactoryService.isIframeApp(app)) {
      return {
        path: app.routingPath,
        component: IframeComponent,
        data: {
          iFrameUrl: this.getIframeUrlForEnvironment(app),
          webAgentUrl: this.getUrlForWebAgent(app),
          pathParam: this.iframeParamService.getPathParamForApp(app),
          getAttributes: () => {
            return this.iframeParamService.getQueryParamsForApp(app);
          },
          appConfiguration: app
        }
      };
    } else {
      // web component app
      return {
        path: app.routingPath,
        component: WebComponentComponent,
        data: {
          webComponentUrls: this.getWebComponentUrlsForEnvironment(app),
          getAttributes: () => {
            return this.webComponentAttributeService.getAttributesForApp(app);
          },
          getEventListeners: this.webComponentEventService.getEventListenersForApp(app),
          appConfiguration: app
        }
      };
    }
  }

  private getIframeUrlForEnvironment(app: IIframeAppConfiguration): string {
    return ConfigurationService.usesIntranetUrls() ? app.iFrameUrls.b2dIntranet : app.iFrameUrls.b2d;
  }

  private getWebComponentUrlsForEnvironment(app: IWebComponentAppConfiguration): string[] {
    const urls = ConfigurationService.usesIntranetUrls()
      ? app.webComponentUrls.b2dIntranet
      : app.webComponentUrls.b2d;
    if (!urls) {
      return [];
    } else if (typeof urls === 'string') {
      return [urls];
    } else {
      return urls;
    }
  }

  private getUrlForWebAgent(app: IIframeAppConfiguration): string {
    if (!app.webAgentSecuredPopupUrls || !this.user) {
      return '';
    }
    return ConfigurationService.usesIntranetUrls()
      ? app.webAgentSecuredPopupUrls.b2dIntranet || ''
      : app.webAgentSecuredPopupUrls.b2d || '';
  }
}
